/**
 * Layering tags (BOSS-REWORK.md §B5).
 *
 * The scheduler's only combination rule was the arena-control exclusion, and
 * that rule is about *legality* — two forced-position patterns can be
 * unresolvable — not about *interest*. Nothing stopped Radial Ring layering
 * over Echo Chorus (two expanding rings), Sweeping Laser over Tracking Beam
 * (two beams) or Grid Barrage over Bullet Board (two grids), and at
 * `layerFromPhase` 0–1, where the late bosses spend most of the fight,
 * redundant pairs were common.
 *
 * A tag names the SHAPE of the threat, not the pattern's fiction: what the
 * player has to do about it. Two cards sharing a tag ask the same question
 * twice, so SpawningSystem penalises — never bans — picking one while the
 * other runs. A penalty rather than a ban because "two rings at once" is
 * occasionally the right answer, and a hard rule would need the
 * all-tags-exhausted fallback the control rule needed.
 *
 * Keyed by pattern NAME rather than declared on each class, for the same
 * reason data/patternCatalog.js is name-keyed: the player learns "Radial
 * Ring", not "RadialRing as constructed by the Iron Cadence", and one central
 * table can be read end-to-end when deciding whether two cards are redundant.
 * A name absent from this table simply carries no tags, which costs it nothing
 * — it is never penalised and never penalises anything else. Desperation
 * finales and the Echo boss's cards are deliberately absent: the scheduler
 * never picks them, so a tag on one would be a claim nothing reads.
 *
 *   radial     — expanding rings/arcs from a point: read the gaps, move out
 *   beam       — a sustained line that sweeps or tracks: leave the lane
 *   ground     — marked floor that becomes dangerous: read the board, reposition
 *   positional — the boss dictates where you may stand at all
 *   summon     — a second body to shoot, or the player's own kit turned around
 */
export const PATTERN_TAGS = {
  // --- radial ---
  'Radial Ring': ['radial'],
  'Echo Chorus': ['radial'],
  'Implosion': ['radial'],
  'Staccato Volley': ['radial'],
  'Regurgitant Volley': ['radial'],
  'Chorus of the Deep': ['radial'],
  'Twin Voice': ['radial'],
  "Sovereign's Wrath": ['radial'],
  'Apotheosis': ['radial'],
  'Sirenreef': ['radial'],
  'Swell': ['radial'],
  'Homing Shard Volley': ['radial'],
  'Waltz of Daggers': ['radial', 'positional'],
  'Ostinato': ['radial'],
  'Composition': ['radial'],
  'Temporal Rewind': ['radial'],
  'Everyone At Once': ['radial', 'summon'],

  // --- beam ---
  'Sweeping Laser': ['beam'],
  'Tracking Beam': ['beam'],
  'Cross Beam Sweep': ['beam'],
  "Conductor's Baton": ['beam'],
  'Petrifying Gaze': ['beam', 'positional'],

  // --- ground ---
  'Grid Barrage': ['ground'],
  'Bullet Board': ['ground'],
  'Area Denial': ['ground'],
  'Skyfall': ['ground'],
  'Ember Wake': ['ground'],
  'Unmade Ground': ['ground'],
  'Prism Minefield': ['ground'],
  'Choral Staves': ['ground', 'positional'],
  'Warding Cores': ['ground'],

  // --- positional ---
  'Telegraphed Charge': ['positional'],
  'Sovereign Sanctum': ['positional'],
  'Throne Divide': ['positional'],
  'Undertow': ['positional'],
  'Riptide Vortex': ['positional'],
  'The Vow': ['positional'],
  'Jealous Embrace': ['positional'],
  "Sovereign's Summons": ['positional'],
  'Echo Step': ['positional'],
  'Hushfall': ['positional'],
  'Grand Pause': ['positional', 'radial'],
  'Stutter Field': ['positional'],
  'The Repast': ['positional'],

  // --- summon ---
  'The Feast': ['summon'],
  'Corrupted Copy': ['summon'],
  'Understudy': ['summon'],
  'Faltering Lantern': ['summon'],
  'Palimpsest': ['summon'],
  'Déjà Vu': ['summon'],
};

/** Tags for one pattern name — always an array, empty for an untagged card. */
export function tagsFor(name) {
  return PATTERN_TAGS[name] ?? [];
}

/**
 * How many tags `name` shares with everything currently running. 0 means the
 * card asks a question nothing on screen is already asking.
 */
export function tagOverlap(name, runningNames) {
  const tags = tagsFor(name);
  if (!tags.length) return 0;
  let overlap = 0;
  for (const other of runningNames) {
    for (const tag of tagsFor(other)) {
      if (tags.includes(tag)) overlap++;
    }
  }
  return overlap;
}
